import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/supabaseClient';

function asId(value) {
  return value === null || value === undefined ? '' : String(value);
}

export function normalizeCompany(company = {}) {
  return {
    ...company,
    id: asId(company.id),
    name: company.name ?? company.nome ?? '',
  };
}

export function normalizeLocation(location = {}) {
  return {
    ...location,
    id: asId(location.id),
    companyId: asId(location.company_id ?? location.companyId ?? location.aziendaId),
    name: location.name ?? location.nome ?? '',
    code: location.code ?? '',
  };
}

export function normalizeDepartment(department = {}) {
  return {
    ...department,
    id: asId(department.id),
    name: department.name ?? department.nome ?? '',
    activeAssignments: department.active_assignments ?? department.activeAssignments ?? 0,
    totalAssignments: department.total_assignments ?? department.totalAssignments ?? 0,
  };
}

export function normalizeLeaveMinimum(item = {}) {
  return {
    ...item,
    locationId: asId(item.locationId ?? item.location_id),
    departmentId: asId(item.departmentId ?? item.department_id),
    minHeadcount: item.minimo ?? item.min_headcount ?? item.minHeadcount ?? null,
  };
}

export function groupLocationsByCompany(locations = []) {
  return locations.reduce((groups, location) => {
    const companyId = location.companyId;
    if (!groups[companyId]) groups[companyId] = [];
    groups[companyId].push(location);
    return groups;
  }, {});
}

export function useSettingsHierarchy({ eagerDepartments = false } = {}) {
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departmentsByLocation, setDepartmentsByLocation] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingDepartments, setLoadingDepartments] = useState({});
  const [error, setError] = useState('');

  const loadHierarchy = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [companyRows, locationRows] = await Promise.all([
        apiFetch('/api/companies'),
        apiFetch('/api/locations'),
      ]);
      const normalizedCompanies = (companyRows || []).map(normalizeCompany);
      const normalizedLocations = (locationRows || []).map(normalizeLocation);
      let departments = {};

      if (eagerDepartments && normalizedLocations.length) {
        const departmentEntries = await Promise.all(
          normalizedLocations.map(async (location) => [
            location.id,
            (await apiFetch(`/api/departments?location_id=${encodeURIComponent(location.id)}`))
              .map(normalizeDepartment),
          ]),
        );
        departments = Object.fromEntries(departmentEntries);
      }

      setCompanies(normalizedCompanies);
      setLocations(normalizedLocations);
      setDepartmentsByLocation(departments);
    } catch (loadError) {
      setError(loadError.message || 'Impossibile caricare la gerarchia organizzativa');
    } finally {
      setLoading(false);
    }
  }, [eagerDepartments]);

  useEffect(() => {
    loadHierarchy();
  }, [loadHierarchy]);

  const loadDepartments = useCallback(async (locationId) => {
    const normalizedId = asId(locationId);
    if (!normalizedId) return [];
    setLoadingDepartments((current) => ({ ...current, [normalizedId]: true }));
    try {
      const rows = await apiFetch(`/api/departments?location_id=${encodeURIComponent(normalizedId)}`);
      const normalized = (rows || []).map(normalizeDepartment);
      setDepartmentsByLocation((current) => ({ ...current, [normalizedId]: normalized }));
      return normalized;
    } finally {
      setLoadingDepartments((current) => ({ ...current, [normalizedId]: false }));
    }
  }, []);

  return {
    companies,
    locations,
    departmentsByLocation,
    locationsByCompany: useMemo(() => groupLocationsByCompany(locations), [locations]),
    loading,
    loadingDepartments,
    error,
    reload: loadHierarchy,
    loadDepartments,
  };
}